import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';
import AppHeader from '../Header/AppHeader';
import './PractitionerDashboard.css';

function PractitionerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [zenId, setZenId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    loadClients();
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const data = await apiClient.getSubscriptionStatus();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getMyClients();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async () => {
    setError('');
    setSuccess('');

    if (!zenId.match(/^ZEN-\d{6}$/)) {
      setError('Bitte geben Sie eine gültige ZEN-ID ein (Format: ZEN-123456)');
      return;
    }

    try {
      await apiClient.requestAccess(zenId);
      setSuccess('Zugriffsanfrage erfolgreich gesendet! Der User wird benachrichtigt.');
      setZenId('');
      setShowAddModal(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message || 'Fehler beim Senden der Anfrage');
    }
  };

  const handleRemoveClient = async (connectionId) => {
    if (!window.confirm('Möchten Sie diese Begleitung wirklich entfernen?')) {
      return;
    }

    try {
      await apiClient.removeClient(connectionId);
      await loadClients();
    } catch (error) {
      console.error('Error removing client:', error);
      alert('Fehler beim Entfernen der Begleitung');
    }
  };

  const handleGoToUserDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoToSettings = () => {
    navigate('/settings');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Lade Begleitungen...</p>
      </div>
    );
  }

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="practitioner-dashboard">
        <main className="practitioner-main">
        {success && (
          <div className="success-message" data-testid="success-message">
            ✅ {success}
          </div>
        )}

        <div className="practitioner-header">
          <div>
            <h2 className="section-title">Meine Begleitungen</h2>
            <p className="section-subtitle">
              {clients.length} {clients.length === 1 ? 'Person' : 'Personen'} in Begleitung
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-add-client"
            data-testid="add-client-button"
          >
            + Neue Begleitung hinzufügen
          </button>
        </div>

        {clients.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🌱</div>
            <h3>Noch keine Begleitungen</h3>
            <p>Fügen Sie Ihre erste Begleitung hinzu, indem Sie deren ZEN-ID eingeben.</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              Erste Begleitung hinzufügen
            </button>
          </div>
        ) : (
          <div className="clients-grid">
            {clients.map((client) => (
              <div key={client.id} className="client-card" data-testid="client-card">
                <div className="client-header">
                  <div className="client-avatar">
                    {client.client_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="client-info">
                    <h3 className="client-name">{client.client_name}</h3>
                    <p className="client-zen-id">{client.client_zen_id}</p>
                  </div>
                </div>

                <div className="client-stats">
                  <div className="stat-item">
                    <span className="stat-label">Gesamt-Aktivierungen</span>
                    <span className="stat-value">{client.total_activations}</span>
                  </div>
                  {client.last_activation && (
                    <div className="stat-item">
                      <span className="stat-label">Letzte Aktivierung</span>
                      <span className="stat-value-small">
                        {client.last_activation.frequency_name}
                      </span>
                    </div>
                  )}
                </div>

                <div className="client-permissions">
                  <p className="permissions-label">Berechtigungen:</p>
                  <div className="permissions-badges">
                    {client.permissions.view_statistics && (
                      <span className="permission-badge">📊 Statistiken</span>
                    )}
                    {client.permissions.view_journal && (
                      <span className="permission-badge">📖 Journal</span>
                    )}
                    {client.permissions.view_mood_data && (
                      <span className="permission-badge">😊 Stimmung</span>
                    )}
                    {client.permissions.recommend_frequencies && (
                      <span className="permission-badge">✨ Empfehlen</span>
                    )}
                    {client.permissions.write_notes && (
                      <span className="permission-badge">📝 Notizen</span>
                    )}
                  </div>
                </div>

                <div className="client-actions">
                  <button
                    onClick={() => navigate(`/practitioner/client/${client.client_user_id}`)}
                    className="btn-view-client"
                    data-testid="view-client-button"
                  >
                    Details ansehen
                  </button>
                  <button
                    onClick={() => handleRemoveClient(client.id)}
                    className="btn-remove-client"
                    data-testid="remove-client-button"
                  >
                    Entfernen
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Client Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Neue Begleitung hinzufügen</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="modal-close"
                  data-testid="close-modal-button"
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <p className="modal-hint">
                  Geben Sie die ZEN-ID der Person ein, die Sie begleiten möchten.
                  Die Person erhält eine Benachrichtigung und kann Ihnen Zugriff gewähren.
                </p>

                {error && (
                  <div className="error-message" data-testid="error-message">
                    ❌ {error}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="zen-id">ZEN-ID</label>
                  <input
                    id="zen-id"
                    type="text"
                    value={zenId}
                    onChange={(e) => setZenId(e.target.value.toUpperCase())}
                    placeholder="ZEN-123456"
                    className="zen-id-input"
                    data-testid="zen-id-input"
                  />
                  <p className="input-hint">Format: ZEN-123456</p>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn-cancel"
                  data-testid="cancel-button"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleAddClient}
                  className="btn-submit"
                  data-testid="submit-button"
                >
                  Zugriffsanfrage senden
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      </div>
    </>
  );
}

export default PractitionerDashboard;
